import { useState } from 'react';
import clsx from 'clsx';
import { Percent } from 'lucide-react';
import { describeArrangement, validateCommission } from '@atink/domain';
import { arrangementForArtist } from '@/domain/commissionEngine';
import type { Artist, CommissionArrangement } from '@/domain/types';

type Kind = CommissionArrangement['kind'];

const KINDS: Array<{ kind: Kind; label: string }> = [
  { kind: 'percentage', label: 'Percentage' },
  { kind: 'flat-day-fee', label: 'Flat day fee' },
  { kind: 'owner-artist', label: 'Keeps 100%' },
];

/** The form's value box for an arrangement: whole percent for a share, dollars for a fee. */
function valueFor(a: CommissionArrangement): string {
  if (a.kind === 'percentage') return String(Math.round(a.artistShare * 100));
  if (a.kind === 'flat-day-fee') return String(a.feeAud);
  return '';
}

/** The artist record a save would write, or null while the form is incomplete. */
function nextArtist(artist: Artist, kind: Kind, value: string): Artist | null {
  const n = Number(value);
  if (kind !== 'owner-artist' && (value.trim() === '' || !Number.isFinite(n))) return null;
  if (kind === 'percentage') {
    const share = Math.round(n) / 100;
    return { ...artist, commissionRate: share, commissionArrangement: { kind, artistShare: share } };
  }
  if (kind === 'flat-day-fee') return { ...artist, commissionArrangement: { kind, feeAud: Math.round(n) } };
  return { ...artist, commissionArrangement: { kind } };
}

/**
 * Per-artist commission on the profile (B9 / 14ym9bv75nk; diagram 10: "each
 * artist's rate — 100% / flat daily / a % — is entered per-artist in their
 * profile"). Owner and Ops edit it; everyone else reads it. The value is only
 * written on Save (never per keystroke), through the server-checked artist
 * write, and the screen reverts if the server refuses.
 */
export default function CommissionEditor({
  artist,
  canEdit,
  onSave,
}: {
  artist: Artist;
  canEdit: boolean;
  onSave: (next: Artist) => void;
}) {
  const current = arrangementForArtist(artist);
  const [kind, setKind] = useState<Kind>(current.kind);
  const [value, setValue] = useState(() => valueFor(current));

  const next = nextArtist(artist, kind, value);
  const valid = !!next && validateCommission(next).ok;
  const changed = !!next && JSON.stringify(arrangementForArtist(next)) !== JSON.stringify(current);

  return (
    <section data-testid="artist-commission">
      <p className="label-small-caps mb-2 flex items-center gap-1.5">
        <Percent size={11} /> Commission
      </p>
      {canEdit ? (
        <>
          <div className="grid grid-cols-3 gap-1.5">
            {KINDS.map((k) => (
              <button
                key={k.kind}
                type="button"
                onClick={() => {
                  setKind(k.kind);
                  setValue(k.kind === current.kind ? valueFor(current) : '');
                }}
                aria-pressed={kind === k.kind}
                data-testid={`artist-commission-kind-${k.kind}`}
                className={clsx(
                  'px-2.5 py-1.5 rounded-md border text-[11px] font-inter uppercase tracking-widest transition',
                  kind === k.kind
                    ? 'border-gold-bright bg-gold/15 text-gold-bright'
                    : 'border-gold-dim/30 text-cream-soft hover:border-gold-bright/60',
                )}
              >
                {k.label}
              </button>
            ))}
          </div>
          {kind !== 'owner-artist' ? (
            <label className="block mt-2 max-w-[200px]">
              <span className="font-inter text-[10px] uppercase tracking-widest text-cream-soft">
                {kind === 'percentage' ? 'Artist share' : 'Fee per day worked'}
              </span>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 font-mono text-xs text-cream-faint">
                  {kind === 'percentage' ? '%' : '$'}
                </span>
                <input
                  type="number"
                  min={0}
                  max={kind === 'percentage' ? 100 : undefined}
                  step={kind === 'percentage' ? 1 : 10}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  data-testid="artist-commission-value"
                  className="w-full bg-ink-deep border border-gold-dim/40 rounded-md pl-6 pr-3 py-1.5 text-sm text-cream font-inter"
                />
              </div>
            </label>
          ) : null}
          <div className="mt-2 flex items-center gap-3">
            <button
              type="button"
              disabled={!valid || !changed}
              onClick={() => next && onSave(next)}
              data-testid="artist-commission-save"
              className="btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Save commission
            </button>
            {next && !valid ? (
              <span className="font-inter text-[11px] text-blood" role="alert">
                {kind === 'percentage' ? 'Enter a share from 0 to 100%.' : 'Enter a fee from $0 to $5,000.'}
              </span>
            ) : null}
          </div>
        </>
      ) : (
        <p className="font-inter text-sm text-cream">{describeArrangement(current)}</p>
      )}
      <p className="font-serif text-[11px] text-cream-soft mt-2">
        Now {describeArrangement(current)}. A change applies to days with no payout yet; days already paid, or
        written as owed, keep the rate they were written at.
      </p>
    </section>
  );
}
